import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Edit2, Save, X } from "lucide-react";
import TextBanner from "@/components/site/TextBanner";

const BANNER_BLANK = {
  banner_id: "",
  banner_type: "image",
  click_url: "",
  img_url: "",
  impression_url: "",
  alt: "",
  cta_label: "",
  brand: "",
  brand_accent: "",
  tagline: "",
  headline: "",
  accent_color: "#FFC81E",
  bg_gradient: "",
  cta_bg: "",
  is_active: true,
  sort_order: 0,
};

export default function BannersManager() {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/banners").then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const data = { ...editing };
    delete data.id; delete data.created_at;
    try {
      if (editing.id) await api.put(`/admin/banners/${editing.id}`, data);
      else await api.post(`/admin/banners`, data);
      setEditing(null);
      load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Failed to save banner — check the fields and try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner? It will disappear from the homepage carousel.")) return;
    await api.delete(`/admin/banners/${id}`);
    load();
  };

  return (
    <div className="space-y-3 mb-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold">Carousel Banners</h2>
          <p className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--sb-text-muted)" }}>
            Edit the rotating affiliate creatives on the homepage — no code needed
          </p>
        </div>
        <button
          data-testid="add-banner-btn"
          onClick={() => setEditing({ ...BANNER_BLANK, sort_order: banners.length })}
          className="sb-btn-primary px-4 py-2 text-xs uppercase inline-flex items-center gap-2"
        >
          <Plus size={14} /> Add Banner
        </button>
      </div>

      {editing && <BannerEditor banner={editing} setBanner={setEditing} onSave={save} onCancel={() => setEditing(null)} />}

      <div className="grid gap-2">
        {banners.map((b) => (
          <div key={b.id} data-testid={`banner-item-${b.banner_id}`} className="sb-card rounded-lg p-4 flex items-center gap-4">
            <div
              className="relative w-40 h-10 rounded overflow-hidden flex-shrink-0 hidden sm:block"
              style={{ background: "rgba(0,0,0,0.3)" }}
            >
              {b.banner_type === "text" ? (
                <div className="absolute inset-0" style={{ transform: "scale(0.55)", transformOrigin: "left center", width: "182%", height: "182%" }}>
                  <TextBanner banner={b} />
                </div>
              ) : (
                b.img_url && <img src={b.img_url} alt={b.alt} className="w-full h-full object-contain" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] uppercase" style={{ color: "var(--sb-text-muted)" }}>
                {b.banner_type} · #{b.sort_order} · {b.is_active ? "ACTIVE" : "HIDDEN"}
              </p>
              <p className="font-display font-bold truncate">{b.banner_id}</p>
              <p className="text-xs truncate" style={{ color: "var(--sb-text-secondary)" }}>{b.alt || b.headline || b.click_url}</p>
            </div>
            <button data-testid={`edit-banner-${b.banner_id}`} onClick={() => setEditing(b)} className="sb-btn-outline p-2"><Edit2 size={14} /></button>
            <button data-testid={`delete-banner-${b.banner_id}`} onClick={() => remove(b.id)} className="sb-btn-outline p-2" style={{ borderColor: "rgba(255,59,48,0.4)", color: "#FF6B61" }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function BannerEditor({ banner, setBanner, onSave, onCancel }) {
  const u = (k, v) => setBanner({ ...banner, [k]: v });
  const isText = banner.banner_type === "text";
  return (
    <div data-testid="banner-editor" className="sb-card rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Banner ID (tracking key, e.g. highbet-summer26)">
        <input data-testid="banner-id-input" className="sb-input" value={banner.banner_id} onChange={(e) => u("banner_id", e.target.value)} />
      </Field>
      <Field label="Type">
        <select data-testid="banner-type-select" className="sb-input" value={banner.banner_type} onChange={(e) => u("banner_type", e.target.value)}>
          <option value="image">Image banner (728×90 creative)</option>
          <option value="text">Text banner (styled headline)</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Click URL (affiliate tracking link)">
          <input data-testid="banner-click-url-input" className="sb-input" value={banner.click_url} onChange={(e) => u("click_url", e.target.value)} />
        </Field>
      </div>

      {isText ? (
        <>
          <Field label="Brand (white part, e.g. HIGH)"><input className="sb-input" value={banner.brand} onChange={(e) => u("brand", e.target.value)} /></Field>
          <Field label="Brand accent (colored part, e.g. BET)"><input className="sb-input" value={banner.brand_accent} onChange={(e) => u("brand_accent", e.target.value)} /></Field>
          <div className="sm:col-span-2">
            <Field label="Headline (£-amounts auto-highlighted, e.g. BET £10 & GET £30 IN FREE BETS)">
              <input data-testid="banner-headline-input" className="sb-input" value={banner.headline} onChange={(e) => u("headline", e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Tagline (small text above headline)"><input className="sb-input" value={banner.tagline} onChange={(e) => u("tagline", e.target.value)} /></Field>
          </div>
          <Field label="Accent color"><input className="sb-input" value={banner.accent_color} onChange={(e) => u("accent_color", e.target.value)} /></Field>
          <Field label="Background (CSS color/gradient)"><input className="sb-input" value={banner.bg_gradient} onChange={(e) => u("bg_gradient", e.target.value)} /></Field>
          <div className="sm:col-span-2">
            <Field label="CTA button background (CSS, optional)"><input className="sb-input" value={banner.cta_bg} onChange={(e) => u("cta_bg", e.target.value)} /></Field>
          </div>
        </>
      ) : (
        <>
          <div className="sm:col-span-2">
            <Field label="Image URL (728×90 creative)">
              <input data-testid="banner-img-url-input" className="sb-input" value={banner.img_url} onChange={(e) => u("img_url", e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Impression pixel URL (optional, from affiliate network)">
              <input className="sb-input" value={banner.impression_url} onChange={(e) => u("impression_url", e.target.value)} />
            </Field>
          </div>
        </>
      )}

      <Field label="Alt text"><input className="sb-input" value={banner.alt} onChange={(e) => u("alt", e.target.value)} /></Field>
      <Field label="CTA label (floating pill, e.g. Free £30)"><input className="sb-input" value={banner.cta_label} onChange={(e) => u("cta_label", e.target.value)} /></Field>
      <Field label="Sort Order"><input type="number" className="sb-input" value={banner.sort_order} onChange={(e) => u("sort_order", parseInt(e.target.value) || 0)} /></Field>
      <Field label="Active">
        <select className="sb-input" value={banner.is_active ? "1" : "0"} onChange={(e) => u("is_active", e.target.value === "1")}>
          <option value="1">Active</option><option value="0">Hidden</option>
        </select>
      </Field>
      <div className="sm:col-span-2 flex gap-2 mt-2">
        <button data-testid="save-banner-btn" onClick={onSave} className="sb-btn-primary px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><Save size={14} /> Save</button>
        <button onClick={onCancel} className="sb-btn-outline px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}
